import React, {useEffect, useRef, useState} from "react";
import {useEventService} from "../services/event-service";
import {useAuth} from "../context/auth";
import HelperService from "../services/helper-service";
import {useGlobalNotificationMessage} from "../context/message";
import {useNotifyEventsService} from "../services/notify-events-service";
import {AppAttendanceChangedType, AppEvents, AppRoutes, AppSessionKeys, AttendanceTypes, LocalStorageKeys} from "../utils/constants";
import {Collapse, Modal} from "react-bootstrap";
import Section from "./section";
import FormatService from "../services/format-service";
import {usePaymentService} from "../services/payment-service";
import StripePaymentMethods from "./stripe-payment-methods";
import StripePaymentModal from "./stripe-payment-modal";
import {getSessionData} from "../modules/session-persistor";
import {useLocation, useNavigate} from "react-router-dom";


export default function EventAttendanceControl({capacity, attendance, price, id, trainerId, event, ...props}) {
    const [showRsvpModal, setShowRsvpModal] = useState(false);
    const [showBookModal, setShowBookModal] = useState(false);
    const [showBookConfirmModal, setShowBookConfirmModal] = useState(false);
    const [showCardManagementModal, setShowCardManagementModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showCancelBookingModal, setShowCancelBookingModal] = useState(false);
    const [showCancelRsvpModal, setShowCancelRsvpModal] = useState(false);
    const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
    const [showClassFullModal, setShowClassFullModal] = useState(false);
    const [showVerifyPhoneModal, setShowVerifyPhoneModal] = useState(false);
    const [showEnableNotificationsModal, setShowEnableNotificationsModal] = useState(false);

    const paymentModal = useRef();
    const es = useEventService();
    const auth = useAuth();
    const {addErrorMessage, setMessage} = useGlobalNotificationMessage();
    const nes = useNotifyEventsService();
    const ps = usePaymentService();
    const navigate = useNavigate();
    const locationService = useLocation();

    useEffect(() => {
        finishPhoneRedirectProcess();
        finishEnableNotificationsRedirectProcess();
        nes.on(AppEvents.paymentMethodsClosed, resolveContinueBooking);
        nes.on(AppEvents.showPaymentMethods, openStripeCardManagement);
        return () => {
            nes.off(AppEvents.paymentMethodsClosed, resolveContinueBooking);
            nes.off(AppEvents.showPaymentMethods, openStripeCardManagement);
        }
    }, []);

    function rsvp(type){
        es.createAttendance({eventId: id, type: type}).then(async (res) => {
            nes.trigger(AppEvents.attendanceChanged, {eventId: id, type: AppAttendanceChangedType.booking});
            setMessage({
                message: 'You have reserved your spot of interest. The facility or FitPro has been notified, and you will receive updates about this event or session. <br /><br />' +
                    'Important: A reservation does not guarantee entry. Final availability is determined by the facility or FitPro.',
                status: 'success',
                title: 'Reservation Confirmed',
                closeBtnShow: true,
                closeBtnClassName: 'btn btn-primary',
                closeBtnText: 'Ok',
            });
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });
    }

    function cancelRsvp(){
        es.cancelAttendance(id, auth.currentUser.id).then(async (res) => {
            nes.trigger(AppEvents.attendanceChanged, {eventId: id, type: AppAttendanceChangedType.cancel});
            setMessage({
                message: 'Your booking has been cancelled.',
                status: 'success',
                title: 'Reservation Cancelled',
                closeBtnShow: true,
                closeBtnClassName: 'btn btn-primary',
                closeBtnText: 'Ok',
            });
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });
    }

    function requestRefund(){
        es.requestRefund(id, auth.currentUser.id).then(async (res) => {
            nes.trigger(AppEvents.attendanceChanged, id);
            setShowRefundModal(false);
            setMessage({
                message: `<p>FitPro has been notified of your refund request.</p>
                            ${event?.trainer?.mobile ?
                    `<p className="text-center mb-5"><h6>Contact FitPro</h6><a href='tel:${event.trainer.mobile}' class="btn btn-outline-primary"><span class="mdi mdi-phone"></span> ${event.trainer.mobile}</a></p>`
                    : ''}`,
                status: 'success',
                title: 'Refund requested',
                closeBtnShow: true,
                closeBtnClassName: 'btn btn-primary',
                closeBtnText: 'Ok',
            });
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });
    }

    function resolveContinueBooking(eventId=null){
        if (eventId?.detail && eventId?.detail!==id){
            return false;
        }
        let continueBooking = getSessionData(AppSessionKeys.continueWithBooking);
        if (!continueBooking || !continueBooking?.eventId || !continueBooking?.attendanceType){ return false; }

        paymentModal?.current?.clearModal();
        book(continueBooking.attendanceType);
    }

    function openStripeCardManagement(eventId=null){
        if (eventId?.detail && eventId?.detail!==id){
            return false;
        }
        setShowBookModal(false);
        setShowCardManagementModal(true);
    }

    function verifyPhoneOrShowModal(bookType){
        if (!auth.currentUser.mobile){
            setShowVerifyPhoneModal(bookType);
            return false;
        }
        return true;
    }
    function updatePhoneRedirect(){
        localStorage.setItem(LocalStorageKeys.PhoneVerificationReturn, JSON.stringify({
            eventId: id,
            path: locationService.pathname,
            timestamp: Date.now(),
            modalToOpen: showVerifyPhoneModal
        }));
        navigate(AppRoutes.profile.path);
    }
    function finishPhoneRedirectProcess(){
        const returnData = JSON.parse(localStorage.getItem(LocalStorageKeys.PhoneVerificationReturn));
        if (returnData && returnData.eventId === id.toString() && returnData.modalToOpen) {
            setTimeout(() => {
                switch(returnData.modalToOpen) {
                    case 'book':
                        openBookModal();
                        break;
                    case 'rsvp':
                        openRsvpModal();
                        break;
                    default:
                        break;
                }
            }, 200);
        }
        setTimeout(() => {
            localStorage.removeItem(LocalStorageKeys.PhoneVerificationReturn);
        }, 200)
    }

    function verifyNotificationsOrShowModal(bookType){
        if (!auth.currentUser.emailNotifications){
            setShowEnableNotificationsModal(bookType);
            return false;
        }
        return true;
    }
    function enableNotificationsRedirect(){
        localStorage.setItem(LocalStorageKeys.EnableNotificationsReturn, JSON.stringify({
            eventId: id,
            path: locationService.pathname,
            timestamp: Date.now(),
            modalToOpen: showEnableNotificationsModal
        }));
        navigate(`${AppRoutes.profile.path}/notifications`);
    }
    function finishEnableNotificationsRedirectProcess(){
        const returnData = JSON.parse(localStorage.getItem(LocalStorageKeys.EnableNotificationsReturn));
        if (returnData && returnData.eventId === id.toString() && returnData.modalToOpen) {
            setTimeout(() => {
                switch(returnData.modalToOpen) {
                    case 'book':
                        openBookModal();
                        break;
                    case 'rsvp':
                        openRsvpModal();
                        break;
                    default:
                        break;
                }
            }, 200);
        }
        setTimeout(() => {
            localStorage.removeItem(LocalStorageKeys.EnableNotificationsReturn);
        }, 200)
    }

    function book(type){
        //verify payment available - show confirm dialog to user before payment
        ps.verifyEventPaymentIntent({eventId: id, attendanceType: type})
            .then((res) => {
                if (res && res.text && res.code) {
                    if ('OK'===res.code){
                        //create payment intent upon confirmation
                        const _paymentData = {
                            eventId: id,
                            attendanceType: type,
                            paymentIntent: null,
                            message: res.text
                        };
                        paymentModal?.current?.openModal(_paymentData);
                        setShowBookModal(false);
                    } else {
                        switch (res.code){
                            case 'invalid_payment_method': {
                                openStripeCardManagement();
                            } break;
                            default: addErrorMessage(res.text); break;
                        }
                    }
                }

            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
            });
    }

    const PricingCell = function({bookType='rsvp'}){
        let pm, pn, cm, cn = null;

        if (price.nonMember!==null){
            pn = price.nonMember===0 ? 'FREE' : '$'+price.nonMember;
            cn = capacity.nonMember===null ? null : capacity.nonMember - capacity.nonMemberBooked;
        }
        if (price.member!==null){
            pm = price.member===0 ? 'FREE' : '$'+price.member;
            cm = capacity.member===null ? null : capacity.member - capacity.memberBooked;
        }

        function callAction(attType){
            if (bookType==='rsvp'){
                rsvp(attType);
            }
            if (bookType==='book'){
                let bookPrice = 0;
                switch (attType){
                    case AttendanceTypes.member: bookPrice = price.member; break;
                    case AttendanceTypes.nonMember: bookPrice = price.nonMember; break;
                    default: break;
                }
                if (bookPrice>0){
                    book(attType);
                } else {
                    rsvp(attType);
                }
            }
        }

        return <>{(pm || pn) && <div className="row text-center">
            {cm!==0 && pm && <div className="col-sm-12 col-md-6">
                <div className="shadow rounded-3 card border-0 p-3 bg-light mb-2">
                    <h5>Prepaid / Member of this Facility</h5>
                    <h5 className="">{pm}</h5>
                    <p className="">{cm===null ? 'Unlimited capacity' : `${cm} ${FormatService.pluralify(cm, 'spot', 'spots', 'spots')} left`}</p>
                    <button className="btn btn-primary fs-7" disabled={cm!==null && cm<=0} onClick={() => callAction(AttendanceTypes.member)}>{bookType==='book' ? 'BOOK NOW!' : 'RESERVE NOW'}</button>
                </div>
            </div>}
            {cn!==0 && pn && <div className="col-sm-12 col-md-6">
                <div className="shadow rounded-3 card border-0 p-3 bg-light">
                    <h5>Pay per event / Walk-in</h5>
                    <h5 className="mb-3">{pn}</h5>
                    <p className="">{cn===null ? 'Unlimited capacity' : `${cn} ${FormatService.pluralify(cn, 'spot', 'spots', 'spots')} left`}</p>
                    <button className="btn btn-primary fs-7" disabled={cn!==null && cn <= 0} onClick={() => callAction(AttendanceTypes.nonMember)}>{bookType==='book' ? 'BOOK NOW!' : 'RESERVE NOW'}</button>
                </div>
            </div>}
        </div>}</>
    }

    const RsvpModal = function () {
        const closeModal = () => setShowRsvpModal(false);

        return <Modal size='xl' show={!!showRsvpModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue col-sm-6 pb-1">Reserve Now</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <h5>I will attend this event as</h5>
                        </div>
                        <PricingCell bookType={'rsvp'} />
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const BookModal = function () {
        const closeModal = () => setShowBookModal(false);

        return <Modal size='xl' show={!!showBookModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue col-sm-6 pb-1">Book</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <h5>I will attend this event as</h5>
                        </div>
                        <PricingCell bookType={'book'} />
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const BookConfirmModal = function () {
        const closeModal = () => setShowBookConfirmModal(false);

        return <Modal size='md' show={!!showBookConfirmModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue col-sm-6 pb-1">Book confirmation</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <h5>I will attend this event as</h5>
                        </div>
                        <PricingCell bookType={'book'} />
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const RefundModal = function () {
        const closeModal = () => setShowRefundModal(false);

        return <Modal size='md' show={!!showRefundModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Request refund</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <p>You are requesting a refund for {event?.course?.name}. FitPro is responsible for issuing any refund due per event cancellation policy.</p>
                        </div>
                        <Collapse in={showCancellationPolicy}><div className="bg-light rounded-2 border-0 p-3 mb-3" dangerouslySetInnerHTML={{__html:event?.cancellationPolicy}}></div></Collapse>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-outline-primary" onClick={()=>setShowCancellationPolicy(!showCancellationPolicy)}>Event cancellation policy</button>
                            <button className="btn btn-primary" onClick={()=>requestRefund()}>Request refund</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const VerifyPhoneModal = function () {
        const closeModal = () => setShowVerifyPhoneModal(false);

        return <Modal size='md' show={!!showVerifyPhoneModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Phone Number Required</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <p>To complete your booking, your service provider needs a working phone number for essential updates and details like waivers or registration.
                                We’ll redirect you to update your profile and then return you here to finish booking.</p>
                        </div>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-primary" onClick={()=>updatePhoneRedirect()}>Update & Continue</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const EnableNotificationsModal = function () {
        const closeModal = () => setShowEnableNotificationsModal(false);

        return <Modal size='md' show={!!showEnableNotificationsModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Notifications Needed</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <p>Without notifications, you could miss critical event updates or required details.
                                Enable in-app or email notifications now — we’ll redirect you to settings and return you here to complete booking.</p>
                        </div>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-primary" onClick={()=>enableNotificationsRedirect()}>Enable & Continue</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const CancelBookingModal = function () {
        const closeModal = () => setShowCancelBookingModal(false);

        return <Modal size='md' show={!!showCancelBookingModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Cancel booking</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <p>FitPro will be notified to provide any refund due per the event cancellation policy.</p>
                        </div>
                        <Collapse in={showCancellationPolicy}><div className="bg-light rounded-2 border-0 p-3 mb-3" dangerouslySetInnerHTML={{__html:event?.cancellationPolicy}}></div></Collapse>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-outline-primary" onClick={()=>setShowCancellationPolicy(!showCancellationPolicy)}>Event cancellation policy</button>
                            <button className="btn btn-primary" onClick={()=>cancelRsvp()}>Confirm cancellation</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const CancelRsvpModal = function () {
        const closeModal = () => setShowCancelRsvpModal(false);

        return <Modal size='md' show={!!showCancelRsvpModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Cancel booking</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <p>The FitPro will be notified.</p>
                        </div>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-primary" onClick={()=>cancelRsvp()}>Confirm cancellation</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const ClassFullModal = function () {
        const closeModal = () => setShowClassFullModal(false);

        return <Modal size='md' show={!!showClassFullModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Event full</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section>
                    <div className="row pb-3 text-center">
                        <div className="col-sm-12 mb-3">
                            <p className="text-secondary"><span style={{"fontSize": "50pt"}} className="mdi mdi-close-circle-outline text-large py-2"></span></p>
                            <p>Sorry, event is already full</p>
                        </div>
                        <div className="col-sm-12 d-flex justify-content-center gap-2">
                            <button className="btn btn-outline-secondary" onClick={()=>closeModal()}>Close</button>
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const CardModal = function () {
        const closeModal = () => {
            nes.trigger(AppEvents.paymentMethodsClosed, id);
            setShowCardManagementModal(false);
        }

        return <Modal size='lg' show={!!showCardManagementModal} onHide={closeModal} centered>
            <Modal.Header closeButton>
                <h4 className="header-stripe-blue pb-1">Manage payment methods</h4>
            </Modal.Header>
            <Modal.Body className="p-5">
                <Section animation={HelperService.getRandomAnimation()}>
                    <div className="row pb-3">
                        <div className="col-sm-12 mb-3">
                            <StripePaymentMethods />
                        </div>
                    </div>
                </Section>
            </Modal.Body>
        </Modal>
    };

    const availablePlaces = HelperService.getEventSpotsLeft(event.capacity);

    function openBookModal(){
        if (!verifyPhoneOrShowModal('book')){
            return false;
        }
        if (!verifyNotificationsOrShowModal('book')){
            return false;
        }
        setShowBookModal(true);
    }

    function openRsvpModal(){
        if (!verifyPhoneOrShowModal('rsvp')){
            return false;
        }
        if (!verifyNotificationsOrShowModal('rsvp')){
            return false;
        }
        setShowRsvpModal(true);
    }

    return (<>

        {auth?.currentUser?.id !== trainerId && <>

            {!event?.removed && <>
                {attendance?.isCancelled===false && <>
                    {price?.isPaymentRequired && attendance.price>0 && <button className="btn btn-primary fs-7"
                                                                               onClick={() => {setShowCancelBookingModal(true)}}>Cancel Booking</button>}
                    {(!price?.isPaymentRequired || attendance.price===0) && <button className="btn btn-primary fs-7"
                                                                                    onClick={() => {setShowCancelRsvpModal(true)}}>Cancel</button>}
                </>}

                {(!attendance || attendance?.isCancelled===true) && <>

                    { availablePlaces===0 && <>
                        <button className="btn btn-secondary text-white fs-7" onClick={()=> {setShowClassFullModal(true)}}><span>Event is full</span></button>
                    </>}

                    { (availablePlaces>0 || availablePlaces===null) && <>

                        { attendance?.isRefundRequestAvailable && <>
                            {attendance?.isRefundRequested &&
                                <button className="btn btn-primary fs-7"
                                        onClick={() => openBookModal()}>{attendance?.isCancelled ? 'Rebook' : <span>Book now!</span>}</button>}
                            {!attendance?.isRefundRequested && <>
                                <button className="btn btn-primary fs-7"
                                        onClick={()=> {setShowRefundModal(true)}}><span>Request Refund</span></button>
                            </>}
                        </>}

                        { !attendance?.isRefundRequestAvailable && <>
                            {price?.isPaymentRequired &&
                                <button className="btn btn-primary fs-7"
                                        onClick={() => openBookModal()}>{attendance?.isCancelled ? 'Rebook' : <span>Book now!</span>}</button>}
                            {!price?.isPaymentRequired && <>
                                <button className="btn btn-primary fs-7"
                                        onClick={() => openRsvpModal()}>{attendance?.isCancelled ? 'Reserve again' : <span>Reserve Now!</span>}</button>
                            </>}
                        </>}

                    </>}

                </>}
            </>}

            <StripePaymentModal ref={paymentModal} />
            {showVerifyPhoneModal && <VerifyPhoneModal />}
            {showEnableNotificationsModal && <EnableNotificationsModal />}
            {showRefundModal && <RefundModal />}
            {showRsvpModal && <RsvpModal />}
            {showBookModal && <BookModal />}
            {showBookConfirmModal && <BookConfirmModal />}
            {showCardManagementModal && <CardModal />}
            {showCancelBookingModal && <CancelBookingModal />}
            {showCancelRsvpModal && <CancelRsvpModal />}
            {showClassFullModal && <ClassFullModal />}

        </>}
    </>);
}
